// §11.13 — the Console.
//
// A log viewer for a desktop that had nowhere to put its own diagnostics (D-191). Built for the
// person chasing something, which decides most of the design:
//
//   • **Follow is a mode you can leave.** It tails by default and stops the moment you scroll up,
//     because a list that yanks you back to the bottom mid-read is unusable. Scrolling back to
//     the bottom resumes it — the same bargain a terminal makes.
//   • **The filter is not destructive.** Filtering hides rows; it never drops them. Widen it
//     again and what you had is still there, which is what lets you narrow aggressively.
//   • **Copy and Export take what you are looking at**, filter and all, so what you paste into an
//     issue is the evidence you were reading and not the whole ring.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ConsoleEntry,
  ConsoleLevel,
  ConsoleShellAccess,
  HangarAPI,
} from '@hangar/shared';
import { COPY } from '@/copy';
import {
  EMPTY_FILTER,
  LEVELS,
  applyFilter,
  countByLevel,
  formatEntries,
  formatEntry,
  formatTime,
  mergeEntries,
  type ConsoleFilter,
} from './model';

/**
 * How close to the bottom still counts as "at the bottom".
 *
 * Not zero: a sub-pixel scroll height, a row growing by a wrapped line, and the browser's own
 * rounding all leave `scrollTop` a hair short, and a strict test would drop out of follow mode
 * on its own the first time a long line arrived.
 */
const BOTTOM_SLACK_PX = 24;

/**
 * The most rows rendered at once.
 *
 * The ring holds 2000 (D-191) and a filter can select all of them; 2000 live DOM rows makes
 * every keystroke in the filter box janky. The *newest* 500 are rendered because this is a tail,
 * and the count of what is hidden above is shown rather than silently dropped.
 */
const RENDER_CAP = 500;

function Row({ entry }: { entry: ConsoleEntry }) {
  return (
    <div className="con-row" data-level={entry.level} data-testid="con-row">
      <span className="con-time">{formatTime(entry.at)}</span>
      <span className="con-level" data-level={entry.level}>
        {entry.level}
      </span>
      <span className="con-source">{entry.source}</span>
      <span className="con-message">
        {entry.appId ? <span className="con-app">{entry.appId}</span> : null}
        {entry.message}
        {entry.detail ? <span className="con-detail">{entry.detail}</span> : null}
      </span>
    </div>
  );
}

export function Console({ api, access }: { api: HangarAPI; access: ConsoleShellAccess }) {
  const sources = useMemo(() => access.sources(), [access]);
  // The sources are mutable rings: they grow without any of their identities changing, so
  // nothing React can compare tells it there is more to render. The tick is that signal, and it
  // is therefore a real dependency of the merge below rather than a way to force a render.
  const [tick, setTick] = useState(0);
  const [filter, setFilter] = useState<ConsoleFilter>(EMPTY_FILTER);
  const [paused, setPaused] = useState(false);
  const [follow, setFollow] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);

  // One subscription per source, torn down together. `paused` is checked inside rather than
  // being a dependency: re-subscribing on every pause would be churn for no gain, and a source
  // that fires while paused should still be *recorded* — it is the render that is frozen, not
  // the log.
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  useEffect(() => {
    const offs = sources.map((source) =>
      source.subscribe(() => {
        if (!pausedRef.current) setTick((value) => value + 1);
      }),
    );
    return () => {
      for (const off of offs) off();
    };
  }, [sources]);

  // Unpausing has to catch up: entries arrived while the render was frozen and nothing will
  // fire again until the next one does.
  useEffect(() => {
    if (!paused) setTick((value) => value + 1);
  }, [paused]);

  const all = useMemo(() => mergeEntries(sources), [sources, tick]);
  const shown = useMemo(() => applyFilter(all, filter), [all, filter]);
  const counts = useMemo(() => countByLevel(all), [all]);
  const rendered = shown.slice(-RENDER_CAP);
  const hidden = shown.length - rendered.length;

  useEffect(() => {
    if (!follow || paused) return;
    const node = scroller.current;
    if (node) node.scrollTop = node.scrollHeight;
  });

  const onScroll = useCallback(() => {
    const node = scroller.current;
    if (!node) return;
    const atBottom = node.scrollHeight - node.scrollTop - node.clientHeight <= BOTTOM_SLACK_PX;
    setFollow(atBottom);
  }, []);

  const toggleSource = useCallback((id: string) => {
    setFilter((current) => ({
      ...current,
      hiddenSources: current.hiddenSources.includes(id)
        ? current.hiddenSources.filter((value) => value !== id)
        : [...current.hiddenSources, id],
    }));
  }, []);

  const flash = useCallback((message: string) => {
    setStatus(message);
    window.setTimeout(() => setStatus(null), 4_000);
  }, []);

  const onCopy = useCallback(() => {
    void api.transfer
      .copy({ items: [{ mime: 'text/plain', text: formatEntries(shown) }] })
      .then(() => flash(COPY.console.copied(shown.length)))
      .catch(() => flash(COPY.console.copyFailed));
  }, [api, shown, flash]);

  const onExport = useCallback(() => {
    void access
      .export(formatEntries(shown))
      .then((path) => flash(COPY.console.exported(path)))
      .catch(() => flash(COPY.console.exportFailed));
  }, [access, shown, flash]);

  const onClear = useCallback(() => {
    for (const source of sources) source.clear?.();
    setTick((value) => value + 1);
  }, [sources]);

  return (
    <div className="con" data-testid="console">
      <div className="con-bar">
        <div className="con-sources" role="group" aria-label={COPY.console.sourcesLabel}>
          {sources.map((source) => {
            // The filter names what is *hidden*, so a chip is lit unless its source is in it —
            // and clicking a lit chip hides that source, which is what a lit chip promises.
            const on = !filter.hiddenSources.includes(source.id);
            return (
              <button
                key={source.id}
                type="button"
                className="con-chip"
                aria-pressed={on}
                data-on={on}
                data-testid={`con-source-${source.id}`}
                onClick={() => toggleSource(source.id)}
              >
                {source.label}
              </button>
            );
          })}
        </div>

        <label className="con-level-pick">
          <span className="con-label">{COPY.console.levelLabel}</span>
          <select
            value={filter.minLevel}
            data-testid="con-level"
            onChange={(event) =>
              setFilter((current) => ({
                ...current,
                minLevel: event.target.value as ConsoleLevel,
              }))
            }
          >
            {LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </label>

        <input
          className="con-find"
          type="search"
          value={filter.text}
          placeholder={COPY.console.findPlaceholder}
          aria-label={COPY.console.findPlaceholder}
          data-testid="con-find"
          onChange={(event) =>
            setFilter((current) => ({ ...current, text: event.target.value }))
          }
        />

        <div className="con-actions">
          <button
            type="button"
            aria-pressed={paused}
            data-on={paused}
            data-testid="con-pause"
            onClick={() => setPaused((value) => !value)}
          >
            {paused ? COPY.console.resume : COPY.console.pause}
          </button>
          <button type="button" data-testid="con-copy" onClick={onCopy}>
            {COPY.console.copy}
          </button>
          <button type="button" data-testid="con-export" onClick={onExport}>
            {COPY.console.export}
          </button>
          <button type="button" data-testid="con-clear" onClick={onClear}>
            {COPY.console.clear}
          </button>
        </div>
      </div>

      <div
        className="con-list"
        ref={scroller}
        onScroll={onScroll}
        role="log"
        aria-label={COPY.console.screen}
        data-testid="con-list"
      >
        {hidden > 0 ? <p className="con-truncated">{COPY.console.truncated(hidden)}</p> : null}
        {rendered.length === 0 ? (
          <p className="con-empty" data-testid="con-empty">
            {all.length === 0 ? COPY.console.empty : COPY.console.noMatches}
          </p>
        ) : (
          rendered.map((entry) => <Row key={`${entry.source}:${entry.seq}`} entry={entry} />)
        )}
      </div>

      <footer className="con-status">
        <span data-testid="con-count">{COPY.console.counted(shown.length, all.length)}</span>
        <span className="con-tallies">
          {LEVELS.map((level) => (
            <span key={level} className="con-tally" data-level={level}>
              {counts[level]} {level}
            </span>
          ))}
        </span>
        <span className="con-follow" data-on={follow} data-testid="con-follow">
          {follow ? COPY.console.following : COPY.console.paused}
        </span>
        {status ? (
          <span className="con-flash" role="status" data-testid="con-flash">
            {status}
          </span>
        ) : null}
      </footer>
    </div>
  );
}

/** Exported for the app's own tests. */
export { formatEntry, RENDER_CAP };
